/**
 * Início do agente: a zona, os números de hoje e o botão que interessa.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, UserPlus, FileCheck2, Clock, CheckCircle2 } from 'lucide-react';
import api from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';

const Numero = ({ icone: Icone, cor, valor, rotulo }) => (
  <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
    <span className={`grid place-items-center w-8 h-8 rounded-lg mb-2 ${cor}`}>
      <Icone className="w-4 h-4" />
    </span>
    <div className="text-2xl font-bold tabular-nums leading-none">{valor}</div>
    <div className="text-xs text-slate-500 mt-1">{rotulo}</div>
  </div>
);

const FieldHome = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [zonas, setZonas] = useState([]);
  const [meus, setMeus] = useState(null);

  useEffect(() => {
    api.get('/operations/zones').then((r) => setZonas(r.data || [])).catch(() => setZonas([]));
    api.get('/operations/my-workers').then((r) => setMeus(r.data || [])).catch(() => setMeus([]));
  }, []);

  const hoje = new Date().toDateString();
  const doDia = (meus || []).filter((w) => w.recruited_at && new Date(w.recruited_at).toDateString() === hoje);
  const conta = (fases) => (meus || []).filter((w) => fases.includes(w.pipeline_status)).length;

  const primeiro = (user?.full_name || '').trim().split(/\s+/)[0] || 'Agente';
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 19 ? 'Boa tarde' : 'Boa noite';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold">{saudacao}, {primeiro}.</h1>
        <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-1">
          <MapPin className="w-4 h-4 text-blue-400" />
          {zonas.length
            ? zonas.map((z) => z.nome).join(' · ')
            : <span className="text-slate-600">Sem zona atribuída — fale com o Gestor de operações.</span>}
        </div>
      </div>

      <section>
        <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Hoje</h2>
        {meus === null ? (
          <div className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-slate-900 border border-slate-800 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Numero icone={UserPlus} cor="bg-blue-500/15 text-blue-400"
              valor={doDia.length} rotulo="Pré-registados hoje" />
            <Numero icone={FileCheck2} cor="bg-purple-500/15 text-purple-400"
              valor={conta(['verified', 'approved', 'trained', 'active'])} rotulo="Documentos validados" />
            <Numero icone={Clock} cor="bg-amber-500/15 text-amber-400"
              valor={conta(['pending', 'returned'])} rotulo="À espera de mim" />
            <Numero icone={CheckCircle2} cor="bg-green-500/15 text-green-400"
              valor={conta(['active'])} rotulo="Activos" />
          </div>
        )}
      </section>

      {/* Devolvidos primeiro: é trabalho que só o agente pode desbloquear. */}
      {(meus || []).some((w) => w.pipeline_status === 'returned') && (
        <button
          onClick={() => navigate('/admin/workers')}
          className="w-full text-left p-4 rounded-xl bg-amber-500/10 border border-amber-500/30"
        >
          <div className="font-semibold text-amber-300">
            {conta(['returned'])} devolvido(s) para corrigir
          </div>
          <p className="text-xs text-amber-200/70 mt-0.5">
            O Gestor de operações pediu nova recolha. Toque para ver o motivo.
          </p>
        </button>
      )}

      <button
        onClick={() => navigate('/admin/workers/new')}
        disabled={!zonas.length}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-blue-600
                   active:bg-blue-700 disabled:opacity-40 font-bold text-lg"
      >
        <UserPlus className="w-6 h-6" />
        Novo worker
      </button>
    </div>
  );
};

export default FieldHome;
